import { PulseScraperAPI } from "components/Apis/Pulse";
import { currencyFormatter, parseCurrency } from "components/KPIs/helpers";
import { EmptyCellFiller } from "components/KPIs/tabs/individuals/individuals";
import LoadingAnimation from "components/UI/Loader";
import { RankingCard } from "components/UI/rankingCard/RankingCard";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ProgressBar } from "react-bootstrap";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { Collapse } from "reactstrap";
import { getB130Entries, getKPITeams, getPulseMVPS, getReport } from "redux/pulse/B130ReportSlice";
import { setSelectedWeek, setSelectedYear } from "redux/pulse/navigationSlice";
import { fetchScraperOperations } from "redux/scraperOperations/operationsSlice";
import { getTopCustomers } from "../../../redux/pulse/B130ReportSlice";

export const PulseWeeklyReport = () => {
    const dispatch = useDispatch();
    const { selectedWeek, selectedYear } = useSelector((state) => state.pulseNavigation);
    const { filters } = useSelector((state) => state.scraperOperations);
    const { report, loading, error } = useSelector((state) => state.pulseB130Report);
    const { kpiTeams, loading: kpiTeamsLoading, error: kpiTeamsError } = useSelector((state) => state.pulseB130Report);
    const { pulseMVPS, loading: pulseMVPSLoading, error: pulseMVPSError } = useSelector((state) => state.pulseB130Report);
    const { topCustomers, loading: topCustomersLoading, error: topCustomersError } = useSelector((state) => state.pulseB130Report);

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [expandedTeams, setExpandedTeams] = useState({});

    const [formFields, setFormFields] = useState({});
    useEffect(() => {
        if (report?.fields) {
            setFormFields({
                brokerage_load_count_goal: report.fields.brokerage_load_count_goal || "",
                brokerage_load_count: report.fields.brokerage_load_count || "",
                brokerage_revenue_goal:
                    report.fields.brokerage_revenue_goal != null
                        ? currencyFormatter.format(report.fields.brokerage_revenue_goal)
                        : "",
                brokerage_revenue:
                    report.fields.brokerage_revenue != null
                        ? currencyFormatter.format(report.fields.brokerage_revenue)
                        : "",
                internal_service_failures: report.fields.internal_service_failures || "",
                external_service_failures: report.fields.external_service_failures || "",
            });
        }
    }, [report]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormFields((prev) => ({ ...prev, [name]: value }));
    };

    const handleInputBlur = (name, isCurrency = false, isInteger = false) => {
        let rawValue = formFields[name];
        let parsed = isCurrency ? parseFloat(parseCurrency(rawValue)) : isInteger ? parseInt(rawValue, 10) : parseFloat(rawValue);
        if (isNaN(parsed)) return toast.error("Invalid number");

        PulseScraperAPI.patchReport(report.id, {
            fields: { ...report.fields, [name]: parsed },
        });
    };

    const scrollRef = useRef(null);
    const scrollY = useRef(0);

    const toggleTeam = (teamId) => {
        setExpandedTeams(prev => ({
            ...prev,
            [teamId]: !prev[teamId]
        }));
    };

    const captureScroll = () => {
        if (scrollRef.current) {
            scrollY.current = scrollRef.current.scrollTop;
        }
    };

    const restoreScroll = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollY.current;
        }
    };

    useLayoutEffect(() => {
        restoreScroll();
    }, [report]);

    const safeDispatchReport = () => {
        captureScroll();
        dispatch(getReport({ year: selectedYear, week: selectedWeek }))
            .then(() => restoreScroll())
            .catch((err) => console.error('Failed to dispatch report:', err));
    };


    useEffect(() => {
        dispatch(getReport({ year: selectedYear, week: selectedWeek }));
        dispatch(getKPITeams({ year: selectedYear, week: selectedWeek }));
        dispatch(getPulseMVPS({ year: selectedYear, week: selectedWeek }));
        dispatch(getTopCustomers({ year: selectedYear, week: selectedWeek }));
        (async () => {
            try {
                const response = await PulseScraperAPI.getWeekDatesFromYearAndWeek(selectedYear, selectedWeek);
                setStartDate(response.data.start_date);
                setEndDate(response.data.end_date);
                dispatch(getB130Entries({ billing_period__start_date: response.data.start_date, billing_period__end_date: response.data.end_date }));
            } catch (error) {
                console.error("Error fetching week dates:", error);
            }
        })();
    }, [selectedWeek, selectedYear, dispatch]);

    // Updated error handling
    const showError = (error && (error.detail || error.message || Object.keys(error).length > 0)) ||
        (kpiTeamsError && (kpiTeamsError.message || Object.keys(kpiTeamsError).length > 0) ||
            (kpiTeams && kpiTeams.length === 0) || (report && Object.keys(report).length === 0));

    return (
        <div ref={scrollRef} className="pulse-report-scroll-container">
            <div className="pulse-section pt-0 mt-0">
                <div className="pulse-header pt-0 mt-0">
                    <h1>Scraper</h1>
                    <button
                        className="button"
                        onClick={() => {
                            PulseScraperAPI.runScraper({ year: new Date().getFullYear(), week: selectedWeek })
                                .catch((error) => {
                                    error.response?.data?.msg
                                        ? toast.error(error.response.data.msg)
                                        : error.response?.data?.detail
                                            ? toast.error(error.response.data.detail)
                                            : toast.error(error.message);
                                    console.error("Error running scraper:", error);
                                })
                                .then(() => {
                                    dispatch(fetchScraperOperations(filters));
                                })
                        }}
                    >
                        <span className="mr-2">Run</span>
                        <i className="nc-icon nc-button-play" style={{ fontWeight: 'bold' }}></i>
                    </button>
                </div>
                <div className="pulse-body">
                    <p className="text-muted">Configure scraper settings below:</p>
                    <div className="d-flex flex-row">
                        <div className="flex-column">
                            <label>
                                Week
                            </label>
                            <select
                                className="input thin"
                                defaultValue={selectedWeek}
                                onChange={(e) => dispatch(setSelectedWeek(e.target.value))}
                            >
                                {[...Array(52).keys()].map((week) => (
                                    <option key={week} value={week + 1}>
                                        Week {week + 1}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex-column ml-3">
                            <label>
                                Year
                            </label>
                            <select
                                className="input thin"
                                onChange={(e) => dispatch(setSelectedYear(e.target.value))}
                            >
                                {[...Array(5).keys()].map((year) => (
                                    <option key={year} value={new Date().getFullYear() - year}>
                                        {new Date().getFullYear() - year}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <hr />
                </div>
            </div>
            <hr />
            {
                showError ? (
                    <div className="pulse-error">
                        <div className="pulse-section pulse-error">
                            <div className="pulse-header">
                                <h1>Error</h1>
                            </div>
                            <div className="pulse-body">
                                <p className="text-muted">
                                    {error?.detail || error?.message ||
                                        kpiTeamsError?.message ||
                                        JSON.stringify(error || kpiTeamsError) ||
                                        "An error occurred while fetching the report data."}
                                </p>
                                <hr />
                            </div>
                        </div>
                    </div>
                ) : loading || kpiTeamsLoading || pulseMVPSLoading ? (
                    <div className="d-flex justify-content-center align-items-center vh-85">
                        <LoadingAnimation />
                    </div>
                ) : (
                    <>
                        <div className="pulse-section">
                            <div className="pulse-header">
                                <h1>Weekly Pulse Report</h1>
                                {/* <h1 className="ml-2">{selectedYear} - W-{selectedWeek}</h1> */}
                            </div>
                            <div className="pulse-body">
                                {loading ? (
                                    <div className="vh-85 d-flex justify-content-center align-items-center">
                                        <LoadingAnimation />
                                    </div>
                                ) : report ? (
                                    <div className="report-content">
                                        <p className="text-muted">Report for week {selectedWeek} of {selectedYear}</p>
                                        <div>
                                            <table className="w-100 table pulse-report-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '10%' }}>{startDate} - {endDate}</th>
                                                        <th>Load Count Goal</th>
                                                        <th>Load Count</th>
                                                        <th>Load Weekly Change</th>
                                                        <th>Load Progress</th>
                                                        <th>Revenue Goal</th>
                                                        <th>Revenue</th>
                                                        <th>Revenue Weekly Change</th>
                                                        <th>Revenue Progress</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>Company</td>
                                                        <td>
                                                            {
                                                                report.expedite.orders_goal && formFields?.brokerage_load_count_goal
                                                                    ? Math.round(Number(report.expedite.orders_goal) + Number(formFields?.brokerage_load_count_goal))
                                                                    : Math.round(Number(report.expedite.orders_goal || 0))
                                                            }
                                                        </td>
                                                        <td>
                                                            {
                                                                report.expedite.orders && formFields?.brokerage_load_count
                                                                    ? Number(report.expedite.orders) + Number(formFields?.brokerage_load_count)
                                                                    : Math.round(Number(report.expedite.orders || 0))
                                                            }
                                                        </td>
                                                        <td>
                                                            {(() => {
                                                                const previousOrders = Number(report?.previous_period?.expedite?.orders || 0) +
                                                                    Number(report?.previous_period?.fields?.brokerage_load_count || 0);
                                                                const currentOrders = Number(report?.expedite?.orders || 0) +
                                                                    Number(formFields?.brokerage_load_count || 0);
                                                                const changePercentage = previousOrders ? ((currentOrders - previousOrders) / previousOrders) * 100 : 0;

                                                                return (
                                                                    <span className={changePercentage < 0 ? 'text-danger' : 'text-success'}>
                                                                        {changePercentage.toFixed(2)}%
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td>
                                                            {
                                                                report.expedite.orders_goal && formFields?.brokerage_load_count_goal && report.expedite.orders && formFields?.brokerage_load_count
                                                                    ? <ProgressBar
                                                                        now={Math.round(
                                                                            ((Number(report.expedite.orders) + Number(formFields?.brokerage_load_count)) /
                                                                                (Number(report.expedite.orders_goal) + Number(formFields?.brokerage_load_count_goal))) * 100
                                                                        )}
                                                                        label={`${Math.round(
                                                                            ((Number(report.expedite.orders) + Number(formFields?.brokerage_load_count)) /
                                                                                (Number(report.expedite.orders_goal) + Number(formFields?.brokerage_load_count_goal))) * 100
                                                                        )}%`}
                                                                        variant={
                                                                            ((Number(report.expedite.orders) + Number(formFields?.brokerage_load_count)) /
                                                                                (Number(report.expedite.orders_goal) + Number(formFields?.brokerage_load_count_goal))) * 100 < 50
                                                                                ? 'danger'
                                                                                : ((Number(report.expedite.orders) + Number(formFields?.brokerage_load_count)) /
                                                                                    (Number(report.expedite.orders_goal) + Number(formFields?.brokerage_load_count_goal))) * 100 < 80
                                                                                    ? 'warning'
                                                                                    : 'success'
                                                                        }
                                                                    />
                                                                    : <EmptyCellFiller />
                                                            }
                                                        </td>
                                                        <td>
                                                            {
                                                                report.expedite.revenue_goal && formFields?.brokerage_revenue_goal
                                                                    ? currencyFormatter.format(
                                                                        Number(report.expedite.revenue_goal) + Number(parseCurrency(formFields?.brokerage_revenue_goal))
                                                                    )
                                                                    : currencyFormatter.format(Number(report.expedite.revenue_goal || 0))
                                                            }
                                                        </td>
                                                        <td>
                                                            {
                                                                report.expedite.revenue && formFields?.brokerage_revenue
                                                                    ? currencyFormatter.format(
                                                                        Number(report.expedite.revenue) + Number(parseCurrency(formFields?.brokerage_revenue))
                                                                    )
                                                                    : currencyFormatter.format(Number(report.expedite.revenue || 0))
                                                            }
                                                        </td>
                                                        <td>
                                                            {(() => {
                                                                const previousRevenue = Number(report?.previous_period?.expedite?.revenue || 0) +
                                                                    Number(report?.previous_period?.fields?.brokerage_revenue || 0);
                                                                const currentRevenue = Number(report?.expedite?.revenue || 0) +
                                                                    Number(parseCurrency(formFields?.brokerage_revenue) || 0);
                                                                const changePercentage = previousRevenue ? ((currentRevenue - previousRevenue) / previousRevenue) * 100 : 0;

                                                                return (
                                                                    <span className={changePercentage < 0 ? 'text-danger' : 'text-success'}>
                                                                        {changePercentage.toFixed(2)}%
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td>
                                                            {
                                                                report.expedite.revenue_goal && formFields?.brokerage_revenue_goal && report.expedite.revenue && formFields?.brokerage_revenue
                                                                    ? <ProgressBar
                                                                        now={Math.round(
                                                                            ((Number(report.expedite.revenue) + Number(parseCurrency(formFields?.brokerage_revenue))) /
                                                                                (Number(report.expedite.revenue_goal) + Number(parseCurrency(formFields?.brokerage_revenue_goal)))) * 100
                                                                        )}
                                                                        label={`${Math.round(
                                                                            ((Number(report.expedite.revenue) + Number(parseCurrency(formFields?.brokerage_revenue))) /
                                                                                (Number(report.expedite.revenue_goal) + Number(parseCurrency(formFields?.brokerage_revenue_goal)))) * 100
                                                                        )}%`}
                                                                        variant={
                                                                            ((Number(report.expedite.revenue) + Number(parseCurrency(formFields?.brokerage_revenue))) /
                                                                                (Number(report.expedite.revenue_goal) + Number(parseCurrency(formFields?.brokerage_revenue_goal)))) * 100 < 50
                                                                                ? 'danger'
                                                                                : ((Number(report.expedite.revenue) + Number(parseCurrency(formFields?.brokerage_revenue))) /
                                                                                    (Number(report.expedite.revenue_goal) + Number(parseCurrency(formFields?.brokerage_revenue_goal)))) * 100 < 80
                                                                                    ? 'warning'
                                                                                    : 'success'
                                                                        }
                                                                    />
                                                                    : <EmptyCellFiller />
                                                            }
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>Expedite</td>
                                                        <td>{Math.round(report.expedite.orders_goal)}</td>
                                                        <td>{report.expedite.orders}</td>
                                                        <td>
                                                            {(() => {
                                                                const previousOrders = Number(report?.previous_period?.expedite?.orders || 0);
                                                                const currentOrders = Number(report?.expedite?.orders || 0);
                                                                const changePercentage = previousOrders ? ((currentOrders - previousOrders) / previousOrders) * 100 : 0;

                                                                return (
                                                                    <span className={changePercentage < 0 ? 'text-danger' : 'text-success'}>
                                                                        {changePercentage.toFixed(2)}%
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td>
                                                            {report.expedite.orders_goal && report.expedite.orders
                                                                ? <ProgressBar
                                                                    now={Math.round((report.expedite.orders / report.expedite.orders_goal) * 100)}
                                                                    label={`${Math.round((report.expedite.orders / report.expedite.orders_goal) * 100)}%`}
                                                                    variant={
                                                                        (report.expedite.orders / report.expedite.orders_goal) * 100 < 50
                                                                            ? 'danger'
                                                                            : (report.expedite.orders / report.expedite.orders_goal) * 100 < 80
                                                                                ? 'warning'
                                                                                : 'success'
                                                                    }
                                                                />
                                                                : <EmptyCellFiller />
                                                            }
                                                        </td>
                                                        <td>{currencyFormatter.format(report.expedite.revenue_goal)}</td>
                                                        <td>{currencyFormatter.format(report.expedite.revenue)}</td>
                                                        <td>
                                                            {
                                                                (() => {
                                                                    const previousRevenue = Number(report?.previous_period?.expedite?.revenue || 0);
                                                                    const currentRevenue = Number(report?.expedite?.revenue || 0);
                                                                    const changePercentage = previousRevenue ? ((currentRevenue - previousRevenue) / previousRevenue) * 100 : 0;

                                                                    return (
                                                                        <span className={changePercentage < 0 ? 'text-danger' : 'text-success'}>
                                                                            {changePercentage.toFixed(2)}%
                                                                        </span>
                                                                    );
                                                                })()
                                                            }
                                                        </td>
                                                        <td>
                                                            {report.expedite.revenue_goal && report.expedite.revenue
                                                                ? <ProgressBar
                                                                    now={Math.round((report.expedite.revenue / report.expedite.revenue_goal) * 100)}
                                                                    label={`${Math.round((report.expedite.revenue / report.expedite.revenue_goal) * 100)}%`}
                                                                    variant={
                                                                        (report.expedite.revenue / report.expedite.revenue_goal) * 100 < 50
                                                                            ? 'danger'
                                                                            : (report.expedite.revenue / report.expedite.revenue_goal) * 100 < 80
                                                                                ? 'warning'
                                                                                : 'success'
                                                                    }
                                                                />
                                                                : <EmptyCellFiller />
                                                            }
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>Brokerage</td>
                                                        <td>
                                                            <input
                                                                name="brokerage_load_count_goal"
                                                                className="input thin"
                                                                value={formFields.brokerage_load_count_goal || ""}
                                                                onChange={handleInputChange}
                                                                onBlur={() => handleInputBlur("brokerage_load_count_goal")}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                name="brokerage_load_count"
                                                                className="input thin"
                                                                value={formFields.brokerage_load_count || ""}
                                                                onChange={handleInputChange}
                                                                onBlur={() => handleInputBlur("brokerage_load_count")}
                                                            />
                                                        </td>
                                                        <td>
                                                            {(() => {
                                                                const previousOrders = Number(report?.previous_period?.fields?.brokerage_load_count || 0);
                                                                const currentOrders = Number(formFields?.brokerage_load_count || 0);
                                                                const changePercentage = previousOrders ? ((currentOrders - previousOrders) / previousOrders) * 100 : 0;

                                                                return (
                                                                    <span className={changePercentage < 0 ? 'text-danger' : 'text-success'}>
                                                                        {changePercentage.toFixed(2)}%
                                                                    </span>
                                                                )
                                                            })()
                                                            }
                                                        </td>
                                                        <td>
                                                            {formFields?.brokerage_load_count_goal && formFields?.brokerage_load_count && formFields?.brokerage_revenue_goal && formFields?.brokerage_revenue
                                                                ? <ProgressBar
                                                                    now={Math.round(
                                                                        ((Number(formFields?.brokerage_load_count) / Number(formFields?.brokerage_load_count_goal)) * 100)
                                                                    )}
                                                                    label={`${Math.round(
                                                                        ((Number(formFields?.brokerage_load_count) / Number(formFields?.brokerage_load_count_goal)) * 100)
                                                                    )}%`}
                                                                    variant={
                                                                        (Number(formFields?.brokerage_load_count) / Number(formFields?.brokerage_load_count_goal)) * 100 < 50
                                                                            ? 'danger'
                                                                            : (Number(formFields?.brokerage_load_count) / Number(formFields?.brokerage_load_count_goal)) * 100 < 80
                                                                                ? 'warning'
                                                                                : 'success'
                                                                    }
                                                                />
                                                                : <EmptyCellFiller />
                                                            }
                                                        </td>
                                                        <td>
                                                            <input
                                                                name="brokerage_revenue_goal"
                                                                className="input thin"
                                                                value={formFields.brokerage_revenue_goal || ""}
                                                                onChange={handleInputChange}
                                                                onBlur={() => handleInputBlur("brokerage_revenue_goal", true)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                name="brokerage_revenue"
                                                                className="input thin"
                                                                value={formFields.brokerage_revenue || ""}
                                                                onChange={handleInputChange}
                                                                onBlur={() => handleInputBlur("brokerage_revenue", true)}
                                                            />
                                                        </td>
                                                        <td>
                                                            {
                                                                (() => {
                                                                    const previousRevenue = Number(report?.previous_period?.fields?.brokerage_revenue || 0);
                                                                    const currentRevenue = Number(parseCurrency(formFields?.brokerage_revenue) || 0);
                                                                    const changePercentage = previousRevenue ? ((currentRevenue - previousRevenue) / previousRevenue) * 100 : 0;

                                                                    return (
                                                                        <span className={changePercentage < 0 ? 'text-danger' : 'text-success'}>
                                                                            {changePercentage.toFixed(2)}%
                                                                        </span>
                                                                    );
                                                                })()
                                                            }
                                                        </td>
                                                        <td>
                                                            {formFields?.brokerage_revenue_goal && formFields?.brokerage_revenue
                                                                ? <ProgressBar
                                                                    now={Math.round(
                                                                        ((Number(parseCurrency(formFields?.brokerage_revenue)) / Number(parseCurrency(formFields?.brokerage_revenue_goal))) * 100)
                                                                    )}
                                                                    label={`${Math.round(
                                                                        ((Number(parseCurrency(formFields?.brokerage_revenue)) / Number(parseCurrency(formFields?.brokerage_revenue_goal))) * 100)
                                                                    )}%`}
                                                                    variant={
                                                                        (Number(parseCurrency(formFields?.brokerage_revenue)) / Number(parseCurrency(formFields?.brokerage_revenue_goal))) * 100 < 50
                                                                            ? 'danger'
                                                                            : (Number(parseCurrency(formFields?.brokerage_revenue)) / Number(parseCurrency(formFields?.brokerage_revenue_goal))) * 100 < 80
                                                                                ? 'warning'
                                                                                : 'success'
                                                                    }
                                                                />
                                                                : <EmptyCellFiller />
                                                            }
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </ div>
                                        <hr />
                                        <p className="text-muted">Service Failures</p>
                                        <table className="w-100 table pulse-report-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '10%' }}>Classification</th>
                                                    <th>Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Total</td>
                                                    <td>{Number(formFields.internal_service_failures || 0) + Number(formFields.external_service_failures || 0)}</td>
                                                </tr>
                                                <tr>
                                                    <td>Internal</td>
                                                    <td>
                                                        <input
                                                            name="internal_service_failures"
                                                            className="input thin"
                                                            value={formFields.internal_service_failures || ""}
                                                            onChange={handleInputChange}
                                                            onBlur={() => handleInputBlur("internal_service_failures", false, true)}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>External</td>
                                                    <td>
                                                        <input
                                                            name="external_service_failures"
                                                            className="input thin"
                                                            value={formFields.external_service_failures || ""}
                                                            onChange={handleInputChange}
                                                            onBlur={() => handleInputBlur("external_service_failures", false, true)}
                                                        />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <hr />
                                    </div>
                                ) : (
                                    <div className="pulse-error">
                                        <h3>No report data available</h3>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="pulse-section mt-3">
                            <div className="pulse-header">
                                <h1>KPI Pulse Teams</h1>
                            </div>
                            <div className="pulse-body">
                                <p className="text-muted">Weekly team performance — includes only people assigned a quarterly goal in the KPIs section.</p>
                                {kpiTeamsLoading || pulseMVPSLoading || topCustomersLoading? (
                                    <div className="vh-85 d-flex justify-content-center align-items-center">
                                        <LoadingAnimation />
                                    </div>
                                ) : kpiTeamsError ? (
                                    <div className="text-danger text-center">{kpiTeamsError}</div>
                                ) : (
                                    <table className="w-100 table pulse-report-table">
                                        <thead>
                                            <tr>
                                                <th>Team</th>
                                                <th>Load Goal</th>
                                                <th>Load Count</th>
                                                <th>Load Progress</th>
                                                <th>Revenue Goal</th>
                                                <th>Revenue</th>
                                                <th>Revenue Progress</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {kpiTeamsLoading ? (
                                                <tr>
                                                    <td colSpan="3" className="text-center"><LoadingAnimation /></td>
                                                </tr>
                                            ) : kpiTeamsError ? (
                                                <tr>
                                                    <td colSpan="3" className="text-center text-danger ">{kpiTeamsError}</td>
                                                </tr>
                                            ) : kpiTeams.length > 0 ? (
                                                kpiTeams.map((team, index) => (
                                                    <React.Fragment key={index}>
                                                        <tr
                                                            onClick={() => toggleTeam(index)}
                                                            style={{ cursor: 'pointer', transition: '0.1s', borderLeft: expandedTeams[index] ? '3px solid #1b1464' : 'none' }}
                                                            className={expandedTeams[index] ? '' : ''}
                                                        >
                                                            <td style={{ width: '16%' }}>
                                                                <span className="float-left mr-2">
                                                                    {expandedTeams[index]
                                                                        ? <i className="fa fa-chevron-up"></i>
                                                                        : <i className="fa fa-chevron-down"></i>
                                                                    }
                                                                </span>
                                                                [{team.members.length}] {team.name}
                                                            </td>
                                                            <td className="pulse-teams-table-balance">
                                                                {team.loads_goal
                                                                    ? team.loads_goal
                                                                    : <EmptyCellFiller />
                                                                }
                                                            </td>
                                                            <td className="pulse-teams-table-balance">
                                                                {team.load_count
                                                                    ? team.load_count
                                                                    : <>---</>
                                                                }
                                                            </td>
                                                            <td className="pulse-teams-table-balance">
                                                                {team.load_count && team.loads_goal
                                                                    ? <ProgressBar
                                                                        now={(team.load_count / team.loads_goal) * 100}
                                                                        label={`${Math.round((team.load_count / team.loads_goal) * 100)}%`}
                                                                        variant={
                                                                            (team.load_count / team.loads_goal) * 100 < 50
                                                                                ? 'danger'
                                                                                : (team.load_count / team.loads_goal) * 100 < 80
                                                                                    ? 'warning'
                                                                                    : 'success'
                                                                        }
                                                                    />
                                                                    : <EmptyCellFiller />
                                                                }
                                                            </td>
                                                            <td className="pulse-teams-table-balance">
                                                                {team.revenue_goal
                                                                    ? currencyFormatter.format(team.revenue_goal)
                                                                    : <EmptyCellFiller />
                                                                }
                                                            </td>
                                                            <td className="pulse-teams-table-balance">
                                                                {team.revenue
                                                                    ? currencyFormatter.format(team.revenue)
                                                                    : <>---</>
                                                                }
                                                            </td>
                                                            <td className="pulse-teams-table-balance">
                                                                {team.revenue && team.revenue_goal
                                                                    ? <ProgressBar
                                                                        now={(team.revenue / team.revenue_goal) * 100}
                                                                        label={`${Math.round((team.revenue / team.revenue_goal) * 100)}%`}
                                                                        variant={
                                                                            (team.revenue / team.revenue_goal) * 100 < 50
                                                                                ? 'danger'
                                                                                : (team.revenue / team.revenue_goal) * 100 < 80
                                                                                    ? 'warning'
                                                                                    : 'success'
                                                                        }
                                                                    />
                                                                    : <EmptyCellFiller />
                                                                }
                                                            </td>
                                                        </tr>
                                                        {team.members && team.members.length > 0 && (
                                                            <tr style={{ borderLeft: expandedTeams[index] ? '3px solid #1b1464' : 'none' }}>
                                                                <td colSpan="7" className="p-0 border-0">
                                                                    <Collapse isOpen={expandedTeams[index]}>
                                                                        <table className="table sub-table">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>{team.name} Team Members</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {team.members.map((member, memberIndex) => (
                                                                                    <tr key={memberIndex}>
                                                                                        <td style={{ width: '16%' }}>{member.name}</td>
                                                                                        <td className="pulse-teams-table-balance">
                                                                                            {member.loads_goal
                                                                                                ? member.loads_goal
                                                                                                : <EmptyCellFiller />
                                                                                            }
                                                                                        </td>
                                                                                        <td className="pulse-teams-table-balance">
                                                                                            {member.load_count
                                                                                                ? member.load_count
                                                                                                : <EmptyCellFiller />
                                                                                            }
                                                                                        </td>
                                                                                        <td className="pulse-teams-table-balance">
                                                                                            {member.load_count && member.loads_goal
                                                                                                ? <ProgressBar
                                                                                                    now={(member.load_count / member.loads_goal) * 100}
                                                                                                    label={`${Math.round((member.load_count / member.loads_goal) * 100)}%`}
                                                                                                    variant={
                                                                                                        (member.load_count / member.loads_goal) * 100 < 50
                                                                                                            ? 'danger'
                                                                                                            : (member.load_count / member.loads_goal) * 100 < 80
                                                                                                                ? 'warning'
                                                                                                                : 'success'
                                                                                                    }
                                                                                                />
                                                                                                : <EmptyCellFiller />
                                                                                            }
                                                                                        </td>
                                                                                        <td className="pulse-teams-table-balance"><EmptyCellFiller /></td>
                                                                                        <td className="pulse-teams-table-balance">
                                                                                            {member.revenue
                                                                                                ? currencyFormatter.format(member.revenue)
                                                                                                : <EmptyCellFiller />
                                                                                            }
                                                                                        </td>
                                                                                        <td className="pulse-teams-table-balance"><EmptyCellFiller /></td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </Collapse>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="3" className="text-center">No KPI Teams found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                                <hr />
                                <h3><span className="mvps-badge">Continental MVPs</span></h3>
                                <p className="text-muted">Best performing team members — by loads booked</p>
                                <div className="pulse-mvps-list">
                                    {pulseMVPSLoading ? (
                                        <div className="vh-85 d-flex justify-content-center align-items-center">
                                            <LoadingAnimation />
                                        </div>
                                    ) : pulseMVPSError ? (
                                        <div className="text-danger text-center">{pulseMVPSError}</div>
                                    ) : pulseMVPS.length > 0 ? (
                                        <>
                                            <div className="d-flex flex-wrap justify-content-between">
                                                {
                                                    pulseMVPS.map((mvp, index) => (
                                                        <div className="d-flex">
                                                            <RankingCard
                                                                key={index}
                                                                rank={index + 1}
                                                                name={`${mvp.name}`}
                                                                metrics={{
                                                                    team: mvp.team.name,
                                                                    orders: mvp.billable_orders,
                                                                    revenue: mvp.billable_revenue ? currencyFormatter.format(mvp.billable_revenue) : 'N/A',
                                                                }}
                                                            />
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center">No MVPs found</div>
                                    )}
                                </div>
                                <hr />
                                <h3><span className="mvps-badge">Top Customers</span></h3>
                                <p className="text-muted">Top customers — by revenue</p>

                                <div className="pulse-mvps-list">
                                    {topCustomersLoading ? (
                                        <div className="vh-85 d-flex justify-content-center align-items-center">
                                            <LoadingAnimation />
                                        </div>
                                    ) : topCustomersError ? (
                                        <div className="text-danger text-center">{topCustomersError}</div>
                                    ) : topCustomers.data.length > 0 ? (
                                        <div className="table-responsive">
                                            <table className="table table-striped sub-table">
                                                <thead>
                                                    <tr>
                                                        <th>Rank</th>
                                                        <th>Customer</th>
                                                        <th>Orders</th>
                                                        <th>Revenue</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {topCustomers.data.map((customer, index) => (
                                                        <tr key={index}>
                                                            <td>{index + 1}</td>
                                                            <td>{customer.name}</td>
                                                            <td>{customer.orders_count}</td>
                                                            <td>{customer.total_charges ? currencyFormatter.format(customer.total_charges) : 'N/A'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center">No top customers found</div>
                                    )}
                                </div>

                                <hr />
                                <p className="text-muted">Notes</p>
                                <textarea
                                    className="input autoExpand"
                                    defaultValue={report?.notes || ""}
                                    onChange={(e) => {
                                        // Adjust height dynamically
                                        e.target.style.height = "auto";
                                        e.target.style.height = `${e.target.scrollHeight}px`;
                                    }}
                                    onBlur={(e) => {
                                        PulseScraperAPI.patchReport(
                                            report.id,
                                            { notes: e.target.value }
                                        )
                                    }}
                                >
                                </textarea>
                                <hr />
                            </div>
                        </div>
                    </>
                )
            }
        </div>
    );
};
